import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@atlaskit/button/new';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import Spinner from '@atlaskit/spinner';
import { fetchCmdbObjectsWithSignal } from '../../rest/cmdb-object-picker';
import type { CmdbObject } from '../../rest/cmdb-object-picker';
import { PaginatedObjectsView } from '../cmdb-object-picker-new';

interface Props {
	portalId: number;
	requestTypeId: number;
	fieldId: string;
	fieldValueMap: string[][];
	fieldLabel: string;
	isMulti: boolean;
	limit: number;
	errorMessage?: string;
}

export const AssignScopePreview = ({
	portalId,
	requestTypeId,
	fieldId,
	fieldValueMap,
	fieldLabel,
	isMulti,
	limit,
	errorMessage,
}: Props): JSX.Element => {
	const { formatMessage } = useIntl();
	const [state, setState] = useState<{
		status: 'error' | 'ready';
		objects: CmdbObject[];
		requestKey: string;
	} | null>(null);
	const [retry, setRetry] = useState(0);
	const contextKey = JSON.stringify(fieldValueMap.filter(([id]) => id !== fieldId));
	const maximum = isMulti ? Math.min(limit, 1000) : 1;
	const requestKey = JSON.stringify([portalId, requestTypeId, fieldId, contextKey, maximum, retry]);
	const visibleState = state?.requestKey === requestKey ? state : null;
	useEffect(() => {
		const controller = new AbortController();
		const loadPage = async (objects: CmdbObject[]): Promise<CmdbObject[]> => {
			if (objects.length >= maximum || controller.signal.aborted) return objects;
			const response = await fetchCmdbObjectsWithSignal(
				portalId,
				requestTypeId,
				fieldId,
				'',
				JSON.parse(contextKey) as string[][],
				controller.signal,
				objects.length,
			);
			const next = [...objects, ...response.cmdbObjects.slice(0, maximum - objects.length)];
			return response.hasNextPage && response.cmdbObjects.length > 0 ? loadPage(next) : next;
		};
		const load = () => {
			loadPage([]).then(
				(objects) => {
					if (!controller.signal.aborted) setState({ objects, requestKey, status: 'ready' });
				},
				() => {
					if (!controller.signal.aborted) setState({ requestKey, status: 'error', objects: [] });
				},
			);
		};
		const timer = setTimeout(load, 300);
		return () => {
			clearTimeout(timer);
			controller.abort();
		};
	}, [portalId, requestTypeId, fieldId, contextKey, maximum, requestKey]);
	return (
		<Stack space="space.100">
			<FormattedMessage
				id="cmdb-components.assign-scope-readonly"
				defaultMessage="Objects are assigned automatically. You can’t edit this field."
				description="Explains why an Assets portal field is read only"
			/>
			{!visibleState && (
				<Spinner
					label={formatMessage({
						id: 'cmdb-components.assign-scope-loading',
						defaultMessage: 'Loading assigned objects',
						description: 'Accessible label while loading automatically assigned objects',
					})}
				/>
			)}
			{visibleState?.status === 'error' && (
				<>
					<FormattedMessage
						id="cmdb-components.assign-scope-error"
						defaultMessage="We couldn’t load the assigned objects."
						description="Error loading the automatic Assets object preview"
					/>
					<Button onClick={() => setRetry((value) => value + 1)}>
						<FormattedMessage
							id="cmdb-components.assign-scope-retry"
							defaultMessage="Try again"
							description="Retry the automatic object preview"
						/>
					</Button>
				</>
			)}
			{visibleState?.status === 'ready' && visibleState.objects.length === 0 && (
				<div role="status">
					<FormattedMessage
						id="cmdb-components.assign-scope-empty"
						defaultMessage="No objects found"
						description="Empty assign-scope preview"
					/>
				</div>
			)}
			<PaginatedObjectsView
				key={contextKey}
				isReadOnly
				selectedObjects={visibleState?.objects ?? []}
				searchResults={[]}
				isMulti={isMulti}
				limit={maximum}
				fieldLabel={fieldLabel}
				searchQuery=""
				onSearchChange={() => undefined}
				isLoading={!visibleState}
				hasMore={false}
				onLoadMore={() => undefined}
				onSelectionCommit={() => undefined}
				errorMessage={errorMessage}
			/>
		</Stack>
	);
};
